'use client';

import { Check, Clock, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import type { getPendingUploadSendCopy } from '@/i18n/pending-upload-send-copy';
import { formatComposerUploadProgress } from '@/lib/uploads/composer-upload-progress';
import { cn } from '@/lib/utils';
import {
  formatPendingUploadBytes,
  formatPendingUploadSize,
  type PendingUploadFailureKind,
  type PendingUploadFileView,
} from './pending-upload-send-view-state';

type Copy = ReturnType<typeof getPendingUploadSendCopy>;

/**
 * The one tone for an upload that needs a hand (#4844): the warning amber
 * the task row uses, in the message and the pill too. It is recoverable
 * (Retry/Remove), so never the destructive red.
 */
export const UPLOAD_ATTENTION_TONE_CLASS = 'text-(--ref-amber)';

export function pendingUploadFailureText(
  copy: Copy,
  failure: PendingUploadFailureKind | null
) {
  switch (failure) {
    case 'network':
      return copy.failureNetwork;
    case 'server':
      return copy.failureServer;
    case 'too_large':
      return copy.failureTooLarge;
    case 'file_unavailable':
      return copy.failureUnavailable;
    default:
      return copy.failureUnknown;
  }
}

/** A determinate ring: it fills with the bytes, it never spins. */
function ProgressRing({ ratio }: { ratio: number }) {
  const circumference = 2 * Math.PI * 6;
  return (
    <svg viewBox="0 0 16 16" className="size-3.5 -rotate-90" aria-hidden>
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        strokeWidth="2"
        className="stroke-foreground/12"
      />
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - ratio)}
        className="stroke-foreground/70 transition-[stroke-dashoffset] duration-[var(--imai-duration-swap)] ease-[var(--imai-ease)] motion-reduce:transition-none"
      />
    </svg>
  );
}

function StatusMark({ file }: { file: PendingUploadFileView }) {
  if (file.state === 'ready')
    return <Check className="text-foreground/70 size-3.5" aria-hidden />;
  if (file.state === 'failed')
    return (
      <TriangleAlert
        className={cn('size-3.5', UPLOAD_ATTENTION_TONE_CLASS)}
        aria-hidden
      />
    );
  if (file.state === 'waiting')
    return <Clock className="text-muted-foreground size-3.5" aria-hidden />;
  // Every byte is sent and the server is saving it: live, so it moves.
  if (file.finishing)
    return <Loader className="text-muted-foreground size-3.5" />;
  return (
    <ProgressRing
      ratio={file.sizeBytes > 0 ? file.loadedBytes / file.sizeBytes : 0}
    />
  );
}

/**
 * One file of the waiting message: its name, size or live progress, and the
 * controls the sender can use on it. Viewers who didn't send it get none.
 */
export function PendingUploadSendFileRow({
  file,
  copy,
  locale,
  busy,
  onRetry,
  onRemove,
}: {
  file: PendingUploadFileView;
  copy: Copy;
  locale: string;
  busy: boolean;
  onRetry: (file: PendingUploadFileView) => void;
  onRemove: (file: PendingUploadFileView) => void;
}) {
  const progress = formatComposerUploadProgress(
    { loadedBytes: file.loadedBytes, totalBytes: file.sizeBytes },
    locale
  );
  const meta =
    file.state === 'uploading'
      ? file.finishing
        ? copy.fileFinishing
        : `${progress.percent} · ${formatPendingUploadBytes(file.loadedBytes, file.sizeBytes, locale)}`
      : file.state === 'waiting'
        ? copy.fileWaiting
        : formatPendingUploadSize(file.sizeBytes, locale);
  return (
    <li
      data-pending-upload-file={file.state}
      className="flex min-h-10 min-w-0 items-center gap-2.5 py-1.5 ps-3 pe-1.5"
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        <StatusMark file={file} />
        {file.state === 'ready' ? (
          <span className="sr-only">{copy.fileReady}</span>
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm">
            {file.filename}
          </span>
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {meta}
          </span>
        </span>
        {file.state === 'failed' ? (
          <span className="text-muted-foreground text-xs text-pretty">
            {pendingUploadFailureText(copy, file.failure)}
          </span>
        ) : null}
      </span>
      {file.canRetry || file.canRemove ? (
        <span className="flex shrink-0 items-center gap-1">
          {file.canRetry ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={busy}
              aria-busy={busy}
              aria-label={copy.retryFile.replace('{name}', file.filename)}
              onClick={() => onRetry(file)}
            >
              {copy.retry}
            </Button>
          ) : null}
          {file.canRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'text-muted-foreground h-7 px-2 text-xs',
                busy && 'opacity-60'
              )}
              disabled={busy}
              aria-busy={busy}
              aria-label={copy.removeFile.replace('{name}', file.filename)}
              onClick={() => onRemove(file)}
            >
              {copy.remove}
            </Button>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}
